import { useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';

type Mode = 'signin' | 'signup' | 'confirm' | 'forgot' | 'reset';

interface Props {
  onClose: () => void;
  /** Which step to open on — "signup" when arriving from a sign-up prompt. */
  initialMode?: Extract<Mode, 'signin' | 'signup'>;
}

export function AuthPanel({ onClose, initialMode = 'signin' }: Props) {
  const { signIn, signUp, confirm, resend, forgotPassword, resetPassword } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
        onClose();
      } else if (mode === 'signup') {
        const { needsConfirmation } = await signUp(email, password, firstName);
        if (needsConfirmation) {
          setMode('confirm');
          setNotice(`We emailed a confirmation code to ${email}.`);
        } else {
          await signIn(email, password);
          onClose();
        }
      } else if (mode === 'confirm') {
        await confirm(email, code);
        await signIn(email, password); // password still in state from sign-up
        onClose();
      } else if (mode === 'forgot') {
        await forgotPassword(email);
        setCode('');
        setPassword('');
        setMode('reset');
        setNotice(`We emailed a reset code to ${email}.`);
      } else {
        // reset
        await resetPassword(email, code, password);
        setPassword('');
        setCode('');
        setMode('signin');
        setNotice('Password reset. Sign in with your new password.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode === 'signin'
      ? 'Sign in'
      : mode === 'signup'
        ? 'Create your account'
        : mode === 'confirm'
          ? 'Confirm your email'
          : mode === 'forgot'
            ? 'Reset your password'
            : 'Set a new password';

  const lede =
    mode === 'confirm'
      ? 'Enter the code from your inbox to finish setting up your account.'
      : mode === 'forgot'
        ? "Enter your account email and we'll send you a reset code."
        : mode === 'reset'
          ? 'Enter the code we emailed and choose a new password.'
          : 'Sync your routines and progress across every device.';

  const submitLabel = busy
    ? 'Working…'
    : mode === 'signin'
      ? 'Sign in'
      : mode === 'signup'
        ? 'Create account'
        : mode === 'confirm'
          ? 'Confirm & sign in'
          : mode === 'forgot'
            ? 'Send reset code'
            : 'Reset password';

  // Reusable password input with a Show/Hide toggle.
  const passwordField = (label: string, autoComplete: string) => (
    <label>
      {label}
      <span className="pw-field">
        <input
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="button"
          className="pw-toggle"
          onClick={() => setShowPassword((v) => !v)}
          aria-pressed={showPassword}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </span>
    </label>
  );

  // Portal to <body> is load-bearing: the header (.site) uses backdrop-filter,
  // which makes it a containing block for position:fixed descendants. Rendered
  // in place, the modal's `inset:0` would resolve to the thin header box instead
  // of the viewport and center too high. Do not inline this back into the header.
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal auth-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 className="auth-title">{title}</h2>
        <p className="auth-lede">{lede}</p>

        <form className="auth-form" onSubmit={handleSubmit}>
          {/* Name — only asked for at sign-up; it's how we greet you afterwards. */}
          {mode === 'signup' && (
            <label>
              First name
              <input
                type="text"
                autoComplete="given-name"
                required
                maxLength={40}
                placeholder="Sam"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </label>
          )}

          {/* Email — shown for every step except entering a sign-up confirmation code. */}
          {mode !== 'confirm' && (
            <label>
              Email
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          )}

          {/* Password (sign in / sign up), with Show/Hide. */}
          {(mode === 'signin' || mode === 'signup') &&
            passwordField('Password', mode === 'signup' ? 'new-password' : 'current-password')}

          {mode === 'signin' && (
            <button type="button" className="linklike pw-forgot" onClick={() => switchTo('forgot')}>
              Forgot password?
            </button>
          )}

          {/* Reset code + a new password. */}
          {mode === 'reset' && (
            <label>
              Reset code
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}
          {mode === 'reset' && passwordField('New password', 'new-password')}

          {/* Sign-up email confirmation code. */}
          {mode === 'confirm' && (
            <label>
              Confirmation code
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}

          {error && <p className="auth-error">{error}</p>}
          {notice && <p className="auth-notice">{notice}</p>}

          <button className="btn primary auth-submit" type="submit" disabled={busy}>
            {submitLabel}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'signin' && (
            <button className="linklike" onClick={() => switchTo('signup')}>
              New here? Create an account
            </button>
          )}
          {mode === 'signup' && (
            <button className="linklike" onClick={() => switchTo('signin')}>
              Already have an account? Sign in
            </button>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <button className="linklike" onClick={() => switchTo('signin')}>
              Back to sign in
            </button>
          )}
          {mode === 'confirm' && (
            <button
              className="linklike"
              onClick={async () => {
                setError(null);
                try {
                  await resend(email);
                  setNotice(`New code sent to ${email}.`);
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not resend code.');
                }
              }}
            >
              Resend code
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );

  function switchTo(next: Mode) {
    setError(null);
    setNotice(null);
    setShowPassword(false);
    setMode(next);
  }
}
